import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconButton } from "@material-ui/core";
import { RefreshRounded } from "@material-ui/icons";
import { Pagination } from "@material-ui/lab";
import { toast } from "react-toastify";
import api from "../../../services/api";
import CircularLoader from "../../UI/Loader";
import RideWishesTable from "./RideWishesTable";
import { COLORS } from "../../../assets/colors";

function RideWishes() {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("all");
  const pageSize = 15;
  const limit = 15;
  const offset = (page - 1) * pageSize;
  const pages = count > 0 ? Math.ceil(count / pageSize) : 0;

  useEffect(() => {
    fetchWishes();
  }, [page, activeTab]);

  const fetchWishes = async () => {
    try {
      setLoading(true);
      const response = await api.get("/ride-wishes", {
        params: { limit, offset, status: activeTab },
      });
      if (response.status === 200) {
        setWishes(response.data.data);
        setCount(response.data.count);
      }
    } catch (error) {
      toast.error("Failed to fetch ride wishes");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.patch(`/ride-wishes/${id}`, { status });
      toast.success(`Ride wish marked as ${status}`);
      fetchWishes();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ride-wishes/${id}`);
      toast.success("Ride wish deleted");
      fetchWishes();
    } catch (err) {
      toast.error("Failed to delete ride wish");
    }
  };

  const handleChange = (event, value) => {
    setPage(value);
  };

  const handleTabChange = (tab) => {
    setPage(1);
    setActiveTab(tab);
  };

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-text">
          <h1>Ride Wishes</h1>
          <p>
            Passengers who couldn't find a ride submitted these requests.
            Use this to understand unmet demand.
          </p>
        </div>
        <HeaderActions>
          <CountBadge>{count} total</CountBadge>
          <IconButton onClick={fetchWishes}>
            <RefreshRounded />
          </IconButton>
        </HeaderActions>
      </PageHeader>

      <TabsWrapper>
        {["all", "pending", "fulfilled", "expired"].map((tab) => (
          <TabButton
            key={tab}
            className={activeTab === tab ? "active" : ""}
            onClick={() => handleTabChange(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </TabButton>
        ))}
      </TabsWrapper>

      <TableCard>
        {loading ? (
          <CircularLoader isLoading={loading} />
        ) : (
          <RideWishesTable
            wishes={wishes}
            onUpdateStatus={handleUpdateStatus}
            onDelete={handleDelete}
          />
        )}
      </TableCard>

      <PaginationWrapper>
        {pages > 1 && wishes?.length >= 1 && (
          <Pagination
            onChange={handleChange}
            size="large"
            page={page}
            count={pages}
            color="primary"
          />
        )}
      </PaginationWrapper>
    </PageWrapper>
  );
}

export default RideWishes;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-text {
    h1 {
      margin: 0;
      font-size: 1.875rem;
      font-weight: 700;
      color: ${COLORS.adminTextPrimary};
    }
    p {
      margin: 0.5rem 0 0 0;
      color: ${COLORS.adminTextSecondary};
      font-size: 1rem;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const CountBadge = styled.span`
  background: ${COLORS.primaryColor};
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  padding: 0.5rem 1.25rem;
  border: 1px solid ${COLORS.borderColor};
  border-radius: 8px;
  background: white;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${COLORS.adminTextSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }

  &.active {
    background: ${COLORS.primaryColor};
    color: white;
    border-color: ${COLORS.primaryColor};
  }
`;

const TableCard = styled.div`
  background: ${COLORS.adminCardBg};
  border-radius: 12px;
  box-shadow: ${COLORS.adminShadow};
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0 3rem 0;
`;
